// React
import { Suspense, useCallback } from 'react'

// React Router Dom
import { Switch, Route as ReactRoute, Redirect, useHistory } from 'react-router-dom'

// Routes
import { routes as allRoutes } from 'routes'
import { IRoute } from './routes/route.interface'

// MRL
import { observer } from 'mobx-react-lite'

// Components
import { BaseOverlayLoader, BaseWrapper } from 'components'

// i18n
import { useTranslation } from 'react-i18next'

// Constant
import { COMMON_MODULE, BACK_OFFICE_MODULE, AUTH_MODULE, ACCOUNT_MODULE } from 'constant/url.constant'

// Utils
import { routeUtils_filterRouteByRoles } from 'utils/common/route.utils'
import { authUtils_getUserUserAccessCodes } from 'utils/auth/auth.utils'

// Lodash
import isEmpty from 'lodash/isEmpty'

// Custom Hook
import { useAuth } from 'hooks'

// Interfaces
import { IAuthAuthenticatedUser } from 'interfaces/auth'

const Route = observer(() => {
  // Hook
  const { t } = useTranslation()
  const { auth_persisted } = useAuth()
  const history = useHistory()

  // Common state
  // ? when using useMemo with this state, state not behaving like you expected :(
  const authenticatedRoutes = routeUtils_filterRouteByRoles(
    allRoutes,
    authUtils_getUserUserAccessCodes((auth_persisted?.auth_authenticatedUser || {}) as IAuthAuthenticatedUser),
  )

  /**
   * @description Render view
   *
   * @param {object|any} propsRoute
   * @param {object} route
   *
   * @return {JSX} JSX
   */
  const renderComponent = useCallback((propsRoute: any, route: IRoute) => {
    return (
      <BaseWrapper {...propsRoute} meta={route.meta} layout={route.layout}>
        <route.component {...propsRoute} meta={route.meta} />
      </BaseWrapper>
    )
    // eslint-disable-next-line
  }, [])

  /**
   * @description Render component
   *
   * @param {object|any} propsRoute
   * @param {object} route
   *
   * @return {JSX|Redirect} JSX | Will be redirected
   */
  const render = useCallback(
    (propsRoute: any, route: IRoute) => {
      // Set Document Title For The Browser
      document.title = `LSP Astra ${route.meta?.attrs?.label ? `- ${t(route.meta?.attrs?.label || '')}` : ''}`

      const whitelistedAuth = ['/auth/change-role']

      // Check if user already authenticated, but want to access AUTH_MODULE
      if (
        route.meta?.module === AUTH_MODULE &&
        !whitelistedAuth.includes(history.location.pathname) &&
        !isEmpty(auth_persisted?.auth_accessToken)
      ) {
        return <Redirect to="/" />
      }

      if (route.meta?.requireAuth && isEmpty(auth_persisted?.auth_accessToken)) {
        return <Redirect to="/auth/login" />
      }

      return renderComponent(propsRoute, route)
    },
    [auth_persisted?.auth_accessToken, t, renderComponent, history.location.pathname],
  )

  /**
   * @description Map Routes
   *
   * @param {IRoute[]} routes
   *
   * @return {IRoute[]} routes
   */
  const mapRoutes = useCallback(
    (routes: IRoute[]) => {
      // Get public routes
      const publicRoutes =
        auth_persisted?.auth_authenticatedUser?.isValidated || !auth_persisted?.auth_accessToken
          ? allRoutes.filter(
              route =>
                !route.meta.module.includes(BACK_OFFICE_MODULE) &&
                route.meta.module !== COMMON_MODULE &&
                route.meta.module !== ACCOUNT_MODULE,
            )
          : []

      // Get common Routes
      const commonRoutes = allRoutes.filter(route => [COMMON_MODULE, ACCOUNT_MODULE].includes(route.meta.module))

      // Merge specific route and public routes
      return [...publicRoutes, ...routes, ...commonRoutes]
    },
    [auth_persisted],
  )

  return (
    <Suspense fallback={<BaseOverlayLoader isLazyLoading={true} />}>
      <Switch>
        {mapRoutes(authenticatedRoutes).map((route: IRoute) => (
          <ReactRoute key={route.path} path={route.path} exact={route.exact} render={props => render(props, route)} />
        ))}
      </Switch>
    </Suspense>
  )
})

export { Route }
